import { useState, useEffect, useCallback, useRef } from 'react';
import cdmApi, { createCancelSource, resolveError, isCancel } from 'services/cdmApi';
import { ErrorModel } from 'models';
import { AxiosRequestConfig } from 'axios';

export type ResultIs<R> = (subject: unknown) => subject is R;

const DEFAULT_CONFIG: Partial<AxiosRequestConfig> = {};

function useCdmApi<R>(
  url: string | null | undefined,
  isResult?: ResultIs<R>,
  config: Pick<AxiosRequestConfig, 'params' | 'data' | 'headers' | 'method'> = DEFAULT_CONFIG,
  auto: boolean = true,
): Readonly<
  [R | null, boolean, ErrorModel | null, (additionalConfig?: Partial<AxiosRequestConfig>) => void]
> {
  const [result, setResult] = useState<R | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<ErrorModel | null>(null);
  const { params, data, headers, method } = config;

  const requestIdRef = useRef(0);

  const handleFetch = useCallback(
    (additionalConfig: Partial<AxiosRequestConfig> = DEFAULT_CONFIG) => {
      if (!url) {
        setResult(null);
        setLoading(false);
        setError(null);

        return;
      }

      // Keep track of this request to prevent updating the states if
      const requestId = requestIdRef.current + 1;
      requestIdRef.current = requestId;

      setLoading(true);
      setError(null);

      const cancelSource = createCancelSource();
      const promise = cdmApi({
        method: method ?? data ? 'POST' : 'GET',
        url,
        cancelToken: cancelSource.token,
        params,
        data,
        headers,
        ...additionalConfig,
      });

      async function fetch(): Promise<void> {
        try {
          const response = await promise;

          if (requestIdRef.current === requestId) {
            if (!isResult || isResult(response.data)) {
              setResult(response.data);
            } else {
              console.error('response was not the expected format');
              setResult(null);
              setError({
                code: 'default',
              });
            }
          }
        } catch (error) {
          if (requestIdRef.current === requestId) {
            if (isCancel(error)) {
              // Ignore cancel
            } else {
              setError(resolveError(error));
            }
          }
        } finally {
          if (requestIdRef.current === requestId) {
            setLoading(false);
          }
        }
      }

      fetch();

      return cancelSource.cancel;
    },
    [data, headers, isResult, method, params, url],
  );

  useEffect(() => {
    if (auto) {
      return handleFetch();
    }
  }, [auto, handleFetch]);

  return [result, loading, error, handleFetch];
}

export default useCdmApi;
